import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';
import { permitService } from '../../../services/permitService';

const DocumentUpload = ({ permitId }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (permitId) {
      loadDocuments();
    }
  }, [permitId]);

  const loadDocuments = async () => {
    if (!permitId) return;
    setLoadingDocs(true);
    try {
      const docs = await permitService?.listDocuments(permitId);
      setDocuments(docs || []);
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = Array?.from(e?.dataTransfer?.files || []);
    if (files?.length > 0) handleFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array?.from(e?.target?.files || []);
    if (files?.length > 0) handleFiles(files);
    e.target.value = '';
  };

  const handleFiles = async (files) => {
    if (!permitId) {
      setError('Cannot upload documents: permit ID is missing');
      return;
    }
    setError('');
    setUploading(true);
    setUploadProgress(0);

    const total = files?.length;
    let completed = 0;

    for (const file of files) {
      try {
        const maxSize = 10 * 1024 * 1024;
        if (file?.size > maxSize) {
          setError(`File "${file?.name}" exceeds 10MB limit`);
          continue;
        }
        await permitService?.uploadDocument(permitId, file);
        completed++;
        setUploadProgress(Math.round((completed / total) * 100));
      } catch (err) {
        setError(`Failed to upload "${file?.name}": ${err?.message}`);
      }
    }

    setUploading(false);
    setTimeout(() => setUploadProgress(null), 1500);
    await loadDocuments();
  };

  const handleView = async (doc) => {
    try {
      setError('');
      const filePath = `${permitId}/${doc?.name}`;
      const url = await permitService?.getDocumentUrl(filePath);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setError('Could not generate a URL for this document.');
      }
    } catch (err) {
      console.error('View error:', err);
      setError(`Failed to view document: ${err?.message}`);
    }
  };

  const handleDownload = async (doc) => {
    try {
      setError('');
      const filePath = `${permitId}/${doc?.name}`;
      const url = await permitService?.getDocumentUrl(filePath);
      if (url) {
        const response = await fetch(url);
        if (!response?.ok) throw new Error(`Download failed: ${response.statusText}`);
        const blob = await response?.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = doc?.name;
        document.body?.appendChild(a);
        a?.click();
        document.body?.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } else {
        setError('Could not generate a download URL for this document.');
      }
    } catch (err) {
      console.error('Download error:', err);
      setError(`Failed to download document: ${err?.message}`);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc?.name}"?`)) return;
    try {
      const filePath = `${permitId}/${doc?.name}`;
      await permitService?.deleteDocument(filePath);
      await loadDocuments();
    } catch (err) {
      setError(`Failed to delete: ${err?.message}`);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
          isDragging
            ? "border-primary bg-primary/5" :"border-border hover:border-primary/50 hover:bg-muted/50"
        )}
      >
        <div className="flex flex-col items-center gap-3">
          <div className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center transition-colors",
            isDragging ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
          )}>
            <Icon name="Upload" size={24} />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground mb-1">
              {isDragging ? 'Drop files here' : 'Drag and drop files here'}
            </p>
            <p className="text-xs text-muted-foreground">or click to browse from your device</p>
          </div>
          <input
            type="file"
            multiple
            onChange={handleFileInput}
            className="hidden"
            id="file-upload"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            disabled={uploading}
          />
          <label htmlFor="file-upload">
            <Button
              variant="outline"
              size="sm"
              iconName="FolderOpen"
              iconPosition="left"
              disabled={uploading}
              asChild
            >
              <span>Browse Files</span>
            </Button>
          </label>
          <p className="text-xs text-muted-foreground">
            PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (Max 10MB)
          </p>
        </div>
      </div>

      {/* Upload Progress */}
      {uploadProgress !== null && (
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icon name="FileText" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">
                {uploadProgress < 100 ? 'Uploading...' : 'Upload complete!'}
              </span>
            </div>
            <span className="text-sm font-semibold text-primary">{uploadProgress}%</span>
          </div>
          <div className="w-full bg-border rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Document List */}
      {loadingDocs ? (
        <div className="flex items-center justify-center py-4">
          <Icon name="Loader2" size={20} className="animate-spin text-primary" />
          <span className="ml-2 text-sm text-muted-foreground">Loading documents...</span>
        </div>
      ) : documents?.length > 0 ? (
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">Uploaded Documents ({documents?.length})</h4>
          <div className="space-y-2">
            {documents?.map((doc, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="FileText" size={18} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{doc?.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {doc?.metadata?.size && (
                      <span className="text-xs text-muted-foreground">{formatFileSize(doc?.metadata?.size)}</span>
                    )}
                    {doc?.created_at && (
                      <>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">{formatDate(doc?.created_at)}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleView(doc)}
                    className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded transition-colors"
                    title="View"
                  >
                    <Icon name="Eye" size={15} />
                  </button>
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded transition-colors"
                    title="Download"
                  >
                    <Icon name="Download" size={15} />
                  </button>
                  <button
                    onClick={() => handleDelete(doc)}
                    className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded transition-colors"
                    title="Delete"
                  >
                    <Icon name="Trash2" size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-4 text-sm text-muted-foreground">
          No documents uploaded yet
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;